/**
 * @file color-ref.ts
 * @description Ссылка на цвет оформления — роль палитры темы (любая из 9) или
 * свой цвет (phase19.16, партия SB120, этап 2; db-design-sb120-stage2 §1,
 * расхождение 2; SB8).
 *
 * Зачем отдельный тип: цвет рамки карточек и цвет свечения может быть и
 * ролью «Кнопки», которой нет ни в `SITE_FILL_ROLES` (заливки), ни в
 * `SITE_TEXT_COLOR_ROLES` (текст). Роль хранится ролью, не цветом: смена
 * палитры перекрашивает сама. «Как у сайта» (линия у рамки, акцент у
 * свечения) — отсутствие ключа у поля, а не вариант значения.
 */

import { z } from "zod";

import { SITE_COLOR_ROLES, siteColorVar, type SiteColors } from "./keys.js";
import { siteFillColorSchema } from "./surface-fill.js";

/** Цвет: роль темы или свой цвет (#rrggbb / #rrggbbaa). */
export const siteColorRefSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), role: z.enum(SITE_COLOR_ROLES) }),
  z.strictObject({ kind: z.literal("custom"), color: siteFillColorSchema }),
]);
export type SiteColorRef = z.infer<typeof siteColorRefSchema>;

/**
 * Значение для CSS: роль — живой `var(--site-<роль>)` (кабинет меняет цвета
 * темы через `setProperty` без пересборки), свой цвет — как есть (схема
 * пропускает только hex, внедрить CSS через данные нельзя).
 */
export function colorRefCss(ref: SiteColorRef): string {
  return ref.kind === "role" ? `var(${siteColorVar(ref.role)})` : ref.color;
}

/** Цвет hex по палитре темы — для расчётов (прозрачность свечения, контраст рамки). */
export function colorRefHex(ref: SiteColorRef, colors: SiteColors): string {
  return ref.kind === "role" ? colors[ref.role] : ref.color;
}
